// The pure half of session replay's browser rules (doc/product-analytics.md §6, B2). It imports
// nothing and touches no globals -- posthog arrives as a `recorder` of two functions -- so
// `node --test` runs the whole policy without a browser
// (packs/assets/spec/javascript/analytics/session_replay.test.js). That is what turns "a payout
// screen is never recorded" into an assertion instead of a hope.
//
// posthog.js is the other half: it owns the posthog-js import and hands this module the recorder.

// The masking configuration, and the reason this file exists at all. posthog-js masks `<input>`
// elements by default and does NOT mask rendered text, and every quota, attainment percentage and
// payout total in this product is rendered text. So the default is exactly wrong here: everything
// starts masked, and nothing is unmasked.
//
// NOTHING CARRIES `data-ph-capture-attribute-unmask`, AND THAT IS A DECISION RATHER THAN AN
// OVERSIGHT. The PostHog project is set to `Total privacy`, which masks all text server-side and
// overrides any unmask attribute in the markup, so shipping them would be markup that does nothing
// today and an exposure the day somebody loosens the project setting. A fully masked recording
// still carries the layout, the clicks, the scrolling and the dead ends, which is what a setup
// funnel is watched for. `packs/core/spec/lints/session_replay_unmask_lint_spec.rb` is what keeps
// the first one anybody adds out of a partial that draws money.
//
// HEADERS AND BODIES ARE NEVER RECORDED. A captured response body here means payout JSON, which is
// structured and searchable and worse than a video of the screen. The project has them off too;
// this is the copy a stale bundle cannot lose.
//
// `maskTextClass` IS PINNED RATHER THAN LEFT TO THE SDK. Every signed-in shell marks its content
// column with it (packs/core/app/views/layouts), so it is load-bearing markup here rather than a
// default worth inheriting -- and rrweb's two rules are easy to confuse. `ph-mask` makes every
// character in a subtree unreadable and keeps the layout; `ph-no-capture` (rrweb's block class,
// which `MoneyHelper` puts on every amount) replaces the subtree with an empty placeholder. The
// shells mask rather than block on purpose: a blocked content column records a grey rectangle, and
// the only two areas a session may be recorded on are recorded to see where somebody got stuck.
export const SESSION_RECORDING_OPTIONS = {
  maskAllInputs: true,
  maskTextSelector: "*",
  maskTextClass: "ph-mask",
  recordHeaders: false,
  recordBody: false,
}

// Two gates, and the page is only half of it.
//
// THE CONSENT GATE IS AN EXPLICIT YES, not merely the absence of a no. posthog-js is initialised
// with `opt_out_capturing_by_default`, so somebody who has never answered the banner sits in a
// rejected-by-default cohort: captured cookielessly, with nothing written to their device. That is
// a fine state to count a pageview in and not a state to record somebody's screen in, so the
// question asked here is `has_opted_in_capturing()`.
//
// THE PAGE GATE IS THE SERVER'S ANSWER, not this module's. Only a page the server decided may be
// recorded renders the marker (packs/analytics/app/helpers/analytics_helper.rb), so a browser
// carrying an already-loaded bundle onto a statement finds no marker and stops.
export function shouldRecord({ pageAllowsReplay, optedIn }) {
  return pageAllowsReplay === true && optedIn === true
}

// THE OTHER THING POSTHOG-JS DOES TO A PAGE, and no line of the masking policy above reaches it.
// Autocapture turns a click into an EVENT carrying the text and the attributes of what was clicked
// and of every element above it, and `maskTextSelector`, `ph-mask` and the project's own
// `Total privacy` are all rules for the recorder alone -- an event property is masked by none of
// them. posthog-js has it on by default (`autocapture: true`), which was somebody clicking a nav
// label on the marketing site until 12.6 put this bundle on two signed-in screens, where the same
// click carries a person's name, a company's name, or the label of a row in a list of people.
//
// THE ANSWER IS THE SERVER'S OWN, READ THE OTHER WAY ROUND. A page allows a recording only because
// somebody is signed in on it, so the marker that says "a recording is allowed here" is the same
// marker that says "do not autocapture here", and there is no second allow-list to keep in step.
// The marketing site is unchanged: it renders no marker, so it autocaptures as it did before.
export function autocaptureAllowed({ pageAllowsReplay }) {
  return pageAllowsReplay !== true
}

// Called on EVERY Turbo navigation, including the ones that leave the allow-list. Stopping is the
// half that matters: Turbo keeps an already-evaluated module alive across a visit, so a reader who
// goes from the setup wizard to their statement is carrying a live recorder into the most sensitive
// page in the product. Nothing in the markup can stop it, because by then the markup is gone.
//
// `stop` on a recorder that was never started is a no-op, so the ordinary case -- a page nobody
// asked to record -- costs one call and changes nothing.
export function applyReplayState({ pageAllowsReplay, optedIn, recorder }) {
  if (!shouldRecord({ pageAllowsReplay, optedIn })) {
    recorder.stop()
    return false
  }

  recorder.start()
  return true
}
